import { useState } from "react";
import { ThumbsDown, Check } from "lucide-react";
import { api } from "../lib/api";
import { getDeviceMeta } from "../lib/deviceMeta";

/**
 * VocabCorrectionRow — surfaces the vocab engine's applied corrections
 * with a one-tap "That's wrong" affordance per correction.
 *
 * The feedback POST is strict metadata-only:
 *   { variant_id, canonical_id, confidence, specialty,
 *     device_class, browser_family, os_family }
 *
 * NO free text ever leaves the client via this endpoint. The full clinical
 * question, surrounding transcript, IP address, and raw user-agent string
 * are not part of the payload. See /app/backend/vocab_feedback.py for the
 * server contract.
 */
function _specialtyOf(_c) {
  // Only the prosthodontic dictionary is registered today. When more
  // specialties are added, each rule can carry its own `.specialty` field.
  return "prosthodontics";
}

export default function VocabCorrectionRow({ corrections = [], originalTranscript = "" }) {
  const [reported, setReported] = useState({}); // { index: 'pending' | 'sent' | 'failed' }

  const report = async (c, i) => {
    if (reported[i]) return;
    // If the vocab engine could not derive an ID for this correction (very
    // rare — e.g. a caller passed a raw {variant, canonical} pair), refuse
    // to send. The backend would reject anyway.
    if (!c.variantId || !c.canonicalId) {
      setReported((r) => ({ ...r, [i]: "failed" }));
      return;
    }
    setReported((r) => ({ ...r, [i]: "pending" }));
    try {
      const dev = getDeviceMeta();
      await api.post("/vocab/feedback", {
        variant_id: c.variantId,
        canonical_id: c.canonicalId,
        confidence: (c.confidence || "medium").toLowerCase(),
        specialty: _specialtyOf(c),
        device_class: dev.device_class,
        browser_family: dev.browser_family,
        os_family: dev.os_family,
      });
      setReported((r) => ({ ...r, [i]: "sent" }));
    } catch (_) {
      setReported((r) => ({ ...r, [i]: "failed" }));
    }
  };

  if (!corrections.length) return null;

  return (
    <div
      data-testid="quick-assist-vocab-corrections"
      className="mt-2 p-2 rounded-md bg-[var(--pa-gold,#d4af37)]/10 border border-[var(--pa-gold,#d4af37)]/30 text-[11px] text-[var(--pa-text-muted,#aaa)]"
    >
      <div className="text-[10px] uppercase tracking-widest text-[var(--pa-gold,#d4af37)] mb-1">
        Vocabulary corrected
      </div>
      {originalTranscript ? (
        <div className="mb-1">
          <span className="line-through text-[var(--pa-text-dim,#666)]">{originalTranscript}</span>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-1.5">
        {corrections.slice(0, 6).map((c, i) => {
          const state = reported[i];
          const label = state === "sent"
            ? "Reported"
            : state === "pending"
              ? "…"
              : "That's wrong";
          const disabled = !!state;
          return (
            <span
              key={`${c.variant}-${i}`}
              data-testid={`quick-assist-vocab-chip-${i}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--pa-bg-2,#0b0b0b)] border border-[var(--pa-border,#333)]"
            >
              <span className="text-[var(--pa-text-dim,#666)] line-through">{c.variant}</span>
              <span className="text-[var(--pa-gold,#d4af37)]">→ {c.canonical}</span>
              <button
                onClick={() => report(c, i)}
                disabled={disabled}
                data-testid={`quick-assist-vocab-report-${i}`}
                aria-label={`Report "${c.variant} → ${c.canonical}" as incorrect`}
                className={`ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] transition min-h-[24px] ${
                  state === "sent"
                    ? "bg-emerald-500/20 text-emerald-300"
                    : state === "failed"
                      ? "bg-red-500/20 text-red-300"
                      : "bg-[var(--pa-border,#333)] text-[var(--pa-text-muted,#aaa)] hover:bg-[var(--pa-border,#333)]/80 hover:text-[var(--pa-text,#fff)]"
                }`}
              >
                {state === "sent" ? <Check size={10} /> : <ThumbsDown size={10} />}
                <span>{label}</span>
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
}
